import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { GetUserOptions, processBulkAction } from 'src/services/WorkflowService'
import { StaticAction } from 'src/utils/StaticAction'
import { UserDropdownOption } from 'src/viewModels/UserDropdownOption'
import { BulkActionItem } from 'src/viewModels/workflow/BulkActionItem'
import { BulkActionResult } from 'src/viewModels/workflow/BulkActionResult'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveBulkProcessAction } from 'src/viewModels/workflow/ObjectiveBulkProcessAction'
import { assignPendValidationSchema } from '../../validationSchemas/AssignPendValidationSchema'
import BulkActionResultDisplay from './BulkActionResultDisplay'

interface IBulkAssignPendModalProps {
  onClose: (actionExecuted: boolean) => void
  isOpen: boolean
  inboxItems: Array<BulkActionItem>
  inboxId: string
  action: InboxActionSummary
}

function BulkAssignPendModal(this: any, props: IBulkAssignPendModalProps) {
  const [executed, setExecuted] = useState(false)
  const [executingBulkAction, setExecutingBulkAction] = useState(false)
  const [results, setResults] = useState(new Array<BulkActionResult>())
  const [userOptions, setUserOptions] = useState(new Array<UserDropdownOption>())

  useEffect(() => {
    const fetchData = async () => {
      if (props.action.staticActionId == StaticAction.ASSIGN) {
        GetUserOptions(props.inboxId, props.action.id.toString())
          .then((response) => {
            setUserOptions(response)
          })
          .catch(() => {
            setUserOptions(new Array<UserDropdownOption>())
          })
      }
    }

    fetchData().catch(() => {})
  }, [])

  const MenuProps = {
    getContentAnchorEl: null,
    PaperProps: {
      style: {
        maxHeight: 400,
      },
    },
  }

  const handlesOnClose = () => {
    props.onClose(executed)
  }

  async function processAction(values: any) {
    setExecutingBulkAction(true)
    const action = props.action

    const data = new ObjectiveBulkProcessAction()
    data.comment = values.comments
    data.bulkInboxItems = props.inboxItems

    if (props.action.staticActionId == StaticAction.ASSIGN) {
      data.assignUser = values.assignUser

      if (values.assignDueDate != undefined) {
        data.assignDueDate = new Date(
          moment(values.assignDueDate, 'YYYY-MM-DD').toISOString()
        )
      }
    } else if (props.action.staticActionId == StaticAction.PEND) {
      data.pendDate = new Date(moment(values.pendDate, 'YYYY-MM-DD').toISOString())
      data.pendNote = values.pendNote
    }

    try {
      const response = await processBulkAction(data, action.id.toString())
      setResults(response)
      setExecutingBulkAction(false)
      setExecuted(true)
    } catch (error) {
      setExecutingBulkAction(false)
    }
  }
  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={props.isOpen!}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return this.onClose()
      }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={props.action.actionName} isModal={true} />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px' }}>
        {executingBulkAction && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Formik
          initialValues={{
            assignUser: '',
            assignDueDate: undefined,
            pendDate: '',
            pendNote: '',
            comments: '',
            requireComment: props.action.requireComment,
            isAssign: props.action.staticActionId === StaticAction.ASSIGN,
            isPend: props.action.staticActionId === StaticAction.PEND,
          }}
          validationSchema={assignPendValidationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={() => {}}
        >
          {({ values, handleChange, handleBlur, errors, isValid, touched }) => (
            <Form>
              {!executed && (
                <Grid container direction="column" spacing={2}>
                  {props.action.staticActionId == StaticAction.ASSIGN && (
                    <>
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          error={touched.assignUser && !!errors.assignUser}
                        >
                          <InputLabel shrink={true} variant="outlined">
                            User *
                          </InputLabel>
                          <Select
                            variant="outlined"
                            input={<OutlinedInput notched label="User *" />}
                            fullWidth
                            id="userOptions"
                            native={true}
                            name="assignUser"
                            value={values.assignUser}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors.assignUser}
                            required={values.isAssign}
                            MenuProps={MenuProps}
                          >
                            <option value="">Select a user</option>
                            {userOptions?.map((user) => (
                              <option value={user.id}>{user.description}</option>
                            ))}
                          </Select>
                          {touched.assignUser && errors.assignUser && (
                            <FormHelperText>{errors.assignUser}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          inputProps={{ id: 'assignDueDate' }}
                          InputLabelProps={{ shrink: true }}
                          type="date"
                          name="assignDueDate"
                          variant="outlined"
                          value={values.assignDueDate}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          label="Due Date"
                          fullWidth
                          error={touched.assignDueDate && !!errors.assignDueDate}
                          helperText={touched.assignDueDate && errors.assignDueDate}
                        />
                      </Grid>
                    </>
                  )}
                  {props.action.staticActionId == StaticAction.PEND && (
                    <Grid item xs={12}>
                      <TextField
                        inputProps={{ id: 'pendDate' }}
                        InputLabelProps={{ shrink: true }}
                        type="date"
                        name="pendDate"
                        variant="outlined"
                        value={values.pendDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Pend Date"
                        fullWidth
                        error={touched.pendDate && !!errors.pendDate}
                        helperText={touched.pendDate && errors.pendDate}
                        required={values.isPend}
                      />
                    </Grid>
                  )}
                  {props.action.staticActionId == StaticAction.PEND && (
                    <Grid item xs={12}>
                      <TextField
                        label="Pend Note"
                        name="pendNote"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required={true}
                        multiline
                        minRows="1"
                        value={values.pendNote}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.pendNote && !!errors.pendNote}
                        helperText={touched.pendNote && errors.pendNote}
                      />
                    </Grid>
                  )}
                  {(props.action.staticActionId == StaticAction.ASSIGN ||
                    props.action.displayComment) && (
                    <Grid item xs={12}>
                      <TextField
                        label="Comments"
                        name="comments"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        required={values.requireComment}
                        fullWidth
                        multiline
                        minRows="4"
                        value={values.comments}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.comments && !!errors.comments}
                        helperText={touched.comments && errors.comments}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
              {executed && !executingBulkAction && (
                <Grid container direction="column" spacing={1}>
                  <Grid item xs={12}>
                    <BulkActionResultDisplay results={results} />
                  </Grid>
                </Grid>
              )}
              <DialogActions>
                {!executed && (
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ marginLeft: '4px' }}
                    disabled={executed || !isValid || executingBulkAction}
                    onClick={() => {
                      processAction(values).then(() => {})
                    }}
                  >
                    Execute
                  </Button>
                )}
                <Button onClick={handlesOnClose} data-cy="close">
                  Close
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default BulkAssignPendModal
